/**
 * TaskDrawer (Â§1.6 Build 2) â€” the expanded task panel: guidance, done-when/timing,
 * actions (mark done / undo / dismiss), attach-only document uploads, and the
 * activity log. Reuses the planner expandable-row language (D-120.1); the rich
 * per-record "have-ready" panel is Build 4.
 */
import { useRef, useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Paperclip, Download, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { applyOptimisticTaskStatus } from "@/lib/executor-task-optimistic";
import { TaskActivityLog, type TaskEvent } from "./TaskActivityLog";
import { EstatePlanZone } from "./task-drawer/EstatePlanZone";
import { WhoCanHelp } from "./task-drawer/WhoCanHelp";
import { TaskLetterZone, type TaskLetter } from "./task-drawer/TaskLetterZone";
import { MilestoneCaptureZone, type DrawerMilestone } from "./task-drawer/MilestoneCaptureZone";
import { GovFormZone, type GovForm } from "./task-drawer/GovFormZone";
import type { ResolvedBinding } from "@shared/task-bindings";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { MapPin } from "lucide-react";
import { SAGE, WARM } from "@shared/brand";

// s126 item 10 (N5): tasks that point the executor to the read-only Where Is Everything
// aggregate (every location the planner recorded). Optional copy line per task.
const WIE_POINTER: Record<string, { copy?: string }> = {
  "secure-property": {},
  "locate-will-and-secure-file": { copy: "Where Is Everything shows every location your loved one recorded." },
};


export interface DrawerTask {
  taskKey: string;
  guidance: string;
  consequenceText: string;
  deadlineRuleText: string | null;
  deadlineOffsetDays: number | null;
  deadlineUnit: string | null;
  /** s233 (DEADLINE-COUNTDOWN-1, D-232.56): "Due 2 Oct 2026 — 30 days after the death" (the one calculator, server-side). */
  dueLine?: string | null;
  status: string;
  attachmentCount: number;
  professionalHelp?: string[];   // s126 item 8 â€” recommendation line in "Who can help"
  // s169 Piece 2 â€” set when this drawer belongs to a PROMOTED per-record task row. Sibling rows
  // share taskKey, so this is what tells the write routes which row the action lands on.
  recordRowId?: string | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function timingLine(t: DrawerTask): string | null {
  if (t.deadlineRuleText) return t.deadlineRuleText;
  if (t.deadlineUnit && t.deadlineOffsetDays != null) {
    const n = t.deadlineOffsetDays;
    if (n === 0) return "Do this right away.";
    return `Target: within ${n} ${n === 1 ? t.deadlineUnit.replace(/s$/, "") : t.deadlineUnit}.`;
  }
  return null;
}

export function TaskDrawer({ clientId, task }: { clientId: string; task: DrawerTask }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dismissing, setDismissing] = useState(false);
  const [dismissNote, setDismissNote] = useState("");

  // s169 Piece 2 â€” the task-row scope key. Promoted per-record rows share a taskKey, so every
  // read and write below must say WHICH row; `null` is the ordinary un-promoted row and keeps
  // the exact URLs and cache keys this drawer used before.
  const recordRowId = task.recordRowId ?? null;
  const scopeQS = recordRowId ? `?recordRowId=${encodeURIComponent(recordRowId)}` : "";
  const scopeBody = { recordRowId };

  const eventsKey = [`/api/executor/${clientId}/tasks/${task.taskKey}/events${scopeQS}`];
  const { data: events = [] } = useQuery<TaskEvent[]>({ queryKey: eventsKey });
  const attachments = events.filter((e) => e.eventType === "attachment");

  // Bound planner data (Â§1.6 Build 4b-2) â€” lazy on drawer open; per-record state derives
  // from the events stream, so an invalidate of eventsKey re-renders sub-item checks.
  const { data: bindingsData, isLoading: bindingsLoading } = useQuery<{ bindings: ResolvedBinding[]; taskLetters?: TaskLetter[]; milestone?: DrawerMilestone | null; govForm?: GovForm | null }>({
    queryKey: [`/api/executor/${clientId}/tasks/${task.taskKey}/bindings`],
  });
  const bindings = bindingsData?.bindings ?? [];
  const taskLetters = bindingsData?.taskLetters ?? [];
  const milestone = bindingsData?.milestone ?? null;
  const govForm = bindingsData?.govForm ?? null;

  const tasksKey = [`/api/executor/${clientId}/tasks`];

  // s169 TASK-ACTION-FEEDBACK-1 â€” background refresh, deliberately NOT awaited.
  //
  // The first cut returned this promise from onSettled so the mutation stayed pending until the
  // refetch landed. That over-locked: Reade's Test 2 found every action button disabled with a
  // not-allowed cursor for several seconds after each click. The reasoning was wrong â€” the
  // OPTIMISTIC flip already shows the new state, so there is nothing for the refetch to gate. The
  // disabled window must match the MUTATION window, not the refetch window.
  //
  // Safety without the await comes from `cancelQueries` in onMutate: an in-flight refetch (from
  // this action or a previous one) is cancelled before the next optimistic write, so a late
  // response cannot clobber the optimistic state or a follow-on action.
  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: eventsKey });
    void qc.invalidateQueries({ queryKey: tasksKey });
  };

  const [actionError, setActionError] = useState<string | null>(null);

  // ðŸ”´ The disabled ATTRIBUTE is not sufficient on its own. `disabled={isPending}` only takes
  // effect after React re-renders, so several clicks landing inside a single tick all read the
  // pre-render value and every one of them fires a real toggle â€” which is how 15 stacked activity
  // events happened on staging. This ref flips SYNCHRONOUSLY in the click handler, so the second
  // click of a double-click is rejected before React is involved at all. The disabled attribute
  // stays as the visible affordance; this is the actual interlock.
  //
  // s170: the hand-rolled ref that used to live here is now `useSingleFlight` — same pattern, same
  // promise-release semantics, extracted because s169 wrote it twice by hand and the two copies had
  // already drifted apart (SINGLE-FLIGHT-PRIMITIVE-1). This file was the reference implementation;
  // its behaviour is unchanged. One instance is shared by done/undo/dismiss deliberately — they are
  // mutually exclusive status actions, so a second one landing mid-flight should be rejected too.
  const actionFlight = useSingleFlight();

  /**
   * Optimistic status flip + rollback, shared by all three status actions.
   * Honest-degrade (the D-168 {sent,reason} principle applied to a mutation): never show a state
   * the server did not reach, and never show nothing while something is happening. On failure the
   * cache is restored to its pre-click value AND the executor is told, rather than the UI silently
   * snapping back and looking like their click missed.
   */
  const optimistic = <TVars,>(status: string) => ({
    onMutate: async () => {
      setActionError(null);
      await qc.cancelQueries({ queryKey: tasksKey });
      const previous = qc.getQueryData(tasksKey);
      if (previous) {
        qc.setQueryData(tasksKey, applyOptimisticTaskStatus(previous as any, task.taskKey, recordRowId, status));
      }
      return { previous };
    },
    onError: (_err: unknown, _vars: TVars, ctx: { previous?: unknown } | undefined) => {
      if (ctx?.previous !== undefined) qc.setQueryData(tasksKey, ctx.previous);
      setActionError("That didn't save. Check your connection and try again.");
    },
    // Background refresh only â€” fired, not awaited (see `invalidateAll`). The interlock is
    // released by `useSingleFlight` when the mutation promise settles, so the disabled window and the
    // lock window are the same window.
    onSettled: () => { invalidateAll(); },
  });

  const markDone = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/executor/${clientId}/tasks/${task.taskKey}/done`, scopeBody)).json(),
    ...optimistic<void>("done"),
  });
  const undo = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/executor/${clientId}/tasks/${task.taskKey}/reopen`, scopeBody)).json(),
    ...optimistic<void>("not_started"),
  });
  const dismiss = useMutation({
    mutationFn: async (note: string) =>
      (await apiRequest("POST", `/api/executor/${clientId}/tasks/${task.taskKey}/dismiss`, note ? { ...scopeBody, note } : scopeBody)).json(),
    ...optimistic<string>("dismissed"),
    onSuccess: () => { setDismissing(false); setDismissNote(""); },
  });

  // ALL task actions are disabled while ANY of them is in flight (ruled scope item 1). Disabling
  // only the clicked button would still allow Done â†’ immediately Dismiss, which stacks two real
  // toggles just as readily as double-clicking one.
  const actionBusy = actionFlight.inFlight || markDone.isPending || undo.isPending || dismiss.isPending;

  // s170: its own lock — attaching is independent of the status actions, and each attach appends a
  // `task_events` attachment row (append-only audit), so a double-fire duplicates the record.
  const uploadFlight = useSingleFlight();

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const data = await fileToBase64(file);
      return (await apiRequest("POST", `/api/executor/${clientId}/tasks/${task.taskKey}/attachments`, {
        ...scopeBody, data, filename: file.name, contentType: file.type,
      })).json();
    },
    onSuccess: invalidateAll,
  });

  const download = async (eventId: string) => {
    const res = await apiRequest("GET", `/api/executor/${clientId}/tasks/${task.taskKey}/attachments/${eventId}/download`);
    const { url } = await res.json();
    if (url) window.open(url, "_blank", "noopener");
  };

  const timing = timingLine(task);
  const isDone = task.status === "done";

  return (
    <div className="px-8 py-6" style={{ color: WARM.charcoal }}>
      {/* Guidance */}
      <p className="text-[15px]" style={{ lineHeight: 1.65 }}>{task.guidance}</p>

      {/* Timing / done-when line */}
      {/* s233 (board 12 v433, the drawer's due line): the date, and how far it is from the death where it counts from it. */}
      {task.dueLine && <p className="text-[13.5px] mt-2" style={{ color: WARM.charcoal }} data-testid="task-due-line">{task.dueLine}</p>}
      {timing && <p className="text-[13px] mt-2" style={{ color: WARM.taupe }}>{timing}</p>}
      {task.consequenceText && (
        <p className="text-[12px] mt-1.5" style={{ color: WARM.taupe }}>Why it matters: {task.consequenceText}</p>
      )}

      {/* FROM THE ESTATE PLAN â€” A/B/E bindings (Â§1.6 Build 4b-2) */}
      <EstatePlanZone bindings={bindings} clientId={clientId} taskKey={task.taskKey} events={events} isLoading={bindingsLoading} />

      {/* s126 item 10: Where Is Everything deep link (read-only aggregate of every recorded location) */}
      {WIE_POINTER[task.taskKey] && (
        <div className="mt-4" data-testid="wie-pointer">
          {WIE_POINTER[task.taskKey].copy && (
            <p className="text-[13px]" style={{ color: WARM.taupe }}>{WIE_POINTER[task.taskKey].copy}</p>
          )}
          <a href={`/category/${CATEGORY_SLUGS.WHERE_IS_EVERYTHING}`}
            className="text-[13px] inline-flex items-center gap-1 mt-1" style={{ color: SAGE[600] }}
            data-testid="wie-link">
            <MapPin className="h-3.5 w-3.5" /> Open Where Is Everything â†’
          </a>
        </div>
      )}

      {/* WHO CAN HELP â€” C/D bindings + s126 professional-help line (hidden when all empty) */}
      <WhoCanHelp bindings={bindings} professionalHelp={task.professionalHelp} />

      {/* LETTERS TO SEND â€” task-level estate letters (CRA, credit bureaus), s126 item 3 */}
      <TaskLetterZone clientId={clientId} taskKey={task.taskKey} taskLetters={taskLetters} events={events} eventsKey={eventsKey} />

      {/* RECORD THE DATE â€” milestone capture on wait-state tasks (s127 Build 1) */}
      {milestone && <MilestoneCaptureZone clientId={clientId} taskKey={task.taskKey} milestone={milestone} />}

      {/* GOVERNMENT FORM â€” framing-appropriate action + requiredDocuments checklist (Build 4, D-132) */}
      {govForm && <GovFormZone clientId={clientId} taskKey={task.taskKey} govForm={govForm} />}


      {/* Action row. s126 Rider 1 (DRAWER-DISMISS-1): while the dismiss composer is open,
          "Mark done" + the opener are hidden â€” the composer owns the moment; Cancel restores them. */}
      <div className="flex items-center gap-3 mt-4">
        {isDone ? (
          <button type="button" onClick={() => actionFlight.runQuiet(() => undo.mutateAsync())} disabled={actionBusy}
            className="rounded-md px-3 py-1.5 text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
            data-testid={`task-undo-${task.taskKey}`}>
            {undo.isPending ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Undoingâ€¦</span>
            ) : "Undo"}
          </button>
        ) : !dismissing ? (
          <>
            <button type="button" onClick={() => actionFlight.runQuiet(() => markDone.mutateAsync())} disabled={actionBusy}
              className="rounded-md px-3.5 py-1.5 text-[13px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: SAGE[600] }}
              data-testid={`task-done-${task.taskKey}`}>
              {/* D-130.5: distinguish the task-level action from the per-record check-off above. */}
              {markDone.isPending ? (
                <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Marking doneâ€¦</span>
              ) : "Mark whole task done"}
            </button>
            <button type="button" onClick={() => setDismissing(true)} disabled={actionBusy}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ border: `1px solid ${WARM.stone}`, color: WARM.taupe }}
              data-testid={`task-dismiss-open-${task.taskKey}`}>
              This doesn't apply
            </button>
          </>
        ) : null}
      </div>

      {/* Honest failure. The optimistic flip has already been rolled back at this point, so
          without this line the UI would simply snap back and read as "my click didn't register" â€”
          which is what made the executor click again in the first place. */}
      {actionError && (
        <p className="mt-2 text-[13px] text-destructive" role="alert" data-testid={`task-action-error-${task.taskKey}`}>
          {actionError}
        </p>
      )}

      {/* Dismiss inline composer â€” textarea + primary confirm ("This doesn't apply") + Cancel. */}
      {dismissing && (
        <div className="mt-2 rounded-md p-3" style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }}>
          <textarea value={dismissNote} onChange={(e) => setDismissNote(e.target.value)} rows={2}
            placeholder="Why doesn't this apply? (optional)"
            className="w-full resize-none rounded-md px-2 py-1.5 text-[13px] outline-none"
            style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }} />
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => actionFlight.runQuiet(() => dismiss.mutateAsync(dismissNote.trim()))} disabled={actionBusy}
              className="rounded-md px-3.5 py-1.5 text-[13px] font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: WARM.charcoal }}
              data-testid={`task-dismiss-confirm-${task.taskKey}`}>
              {dismiss.isPending ? (
                <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Dismissingâ€¦</span>
              ) : "This doesn't apply"}
            </button>
            <button type="button" onClick={() => { setDismissing(false); setDismissNote(""); }} disabled={actionBusy}
              className="text-[13px] disabled:opacity-60" style={{ color: WARM.taupe }}
              data-testid={`task-dismiss-cancel-${task.taskKey}`}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Attachments. Task-level notes use the single activity-log composer (Fix 3, s123 â€”
          removed the duplicate footer "Add note"; the per-record note popover is unrelated). */}
      <div className="mt-4">
        <button type="button" onClick={() => fileInput.current?.click()} disabled={upload.isPending}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium"
          style={{ border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
          data-testid={`task-attach-${task.taskKey}`}>
          <Paperclip className="h-3.5 w-3.5" /> {upload.isPending ? "Attachingâ€¦" : "Attach a document"}
        </button>
        <input ref={fileInput} type="file" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFlight.runQuiet(() => upload.mutateAsync(f)); e.target.value = ""; }} />
        {attachments.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1">
            {attachments.map((a) => (
              <li key={a.id} className="flex items-center gap-2 text-[13px]">
                <span style={{ color: WARM.charcoal }}>{a.payload?.filename ?? "file"}</span>
                <button type="button" onClick={() => download(a.id)} className="inline-flex items-center gap-1" style={{ color: SAGE[600] }}>
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Activity log */}
      <TaskActivityLog clientId={clientId} taskKey={task.taskKey} events={events} eventsKey={eventsKey} />
    </div>
  );
}
